import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, finalize, switchMap, takeUntil } from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { PrintCardService } from "../print-card.service";
import {
  defaultPrintCardPageSizePresetId,
  printCardPageSizePresetsForTemplate,
  type PrintCardBulkDownloadDialogResult,
  type PrintCardPageSizePreset,
  type PrintCardPdfFormatDialogResult,
  type PrintCardTemplateItem,
} from "../print-cards.interface";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import { PrintCardBulkDownloadDialogComponent } from "../print-card-bulk-download-dialog/print-card-bulk-download-dialog.component";
import { PrintCardPdfFormatDialogComponent } from "../print-card-pdf-format-dialog/print-card-pdf-format-dialog.component";
import {
  getHttpErrorMessage,
  getTemplateTypeLabelKey,
} from "../../card-orders/card-order.util";
import {
  canvasListMainPhase,
  type CanvasListMainPhase,
} from "../../shared/canvas-list-view";

@Component({
  selector: "app-print-cards-list",
  templateUrl: "./print-cards-list.component.html",
  styleUrls: ["./print-cards-list.component.scss"],
  standalone: false,
})
export class PrintCardsListComponent implements OnInit, OnDestroy {
  readonly templateTypeLabelKey = getTemplateTypeLabelKey;
  loading = true;
  deleting = false;
  duplicating = false;
  downloading = false;
  downloadingPrinter = false;
  downloadingAllEmployees = false;
  loadingPreview = false;
  employeePickerReady = false;
  activeUpdatingTemplateId: string | null = null;
  previewUpdatingTemplateId: string | null = null;
  approvalUpdatingTemplateId: string | null = null;
  items: PrintCardTemplateItem[] = [];
  selected: PrintCardTemplateItem | null = null;
  previewDocumentHtml: string | null = null;
  previewBackDocumentHtml: string | null = null;
  previewPageWidthCm: number | null = null;
  previewPageHeightCm: number | null = null;
  previewEmployee: EmployeePickerItem | null = null;
  pageSizePresets: PrintCardPageSizePreset[] = [];
  previewPageSizePresetId: string | null = null;
  private preferredTemplateId: string | null = null;
  private readonly previewTrigger$ = new Subject<{
    templateId: string;
    email: string;
    pageSizePresetId?: string;
  } | null>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private printCardService: PrintCardService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.preferredTemplateId =
      this.route.snapshot.queryParamMap.get("templateId")?.trim() || null;
    this.previewTrigger$
      .pipe(
        switchMap((request) => {
          if (!request) {
            return of(null);
          }
          return this.printCardService
            .preview(
              request.templateId,
              request.email,
              request.pageSizePresetId,
            )
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          if (!res) {
            this.loadingPreview = false;
            return;
          }
          const hasDims =
            Number.isFinite(res.pageWidthCm) &&
            Number.isFinite(res.pageHeightCm) &&
            res.pageWidthCm > 0 &&
            res.pageHeightCm > 0;
          const layout = hasDims
            ? {
                pageWidthCm: res.pageWidthCm,
                pageHeightCm: res.pageHeightCm,
              }
            : this.layoutFromSelection();
          const front = res.frontDocumentHtml.trim();
          this.previewPageWidthCm = layout?.pageWidthCm ?? null;
          this.previewPageHeightCm = layout?.pageHeightCm ?? null;
          this.previewDocumentHtml = front.length ? front : null;
          this.previewBackDocumentHtml = res.backDocumentHtml.trim().length
            ? res.backDocumentHtml
            : null;
          this.loadingPreview = false;
        },
        error: () => {
          this.loadingPreview = false;
        },
      });
    this.printCardService
      .prefetchCatalogs()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe();
    this.loading = true;
    forkJoin({
      templates: this.printCardService.getTemplates(),
      presets: this.printCardService
        .getPageSizePresets()
        .pipe(
          catchError(() => of({ presets: [] as PrintCardPageSizePreset[] })),
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ templates, presets }) => {
          this.pageSizePresets = presets.presets ?? [];
          this.items = templates.templates ?? [];
          const preferred = this.preferredTemplateId
            ? this.items.find(
                (item) =>
                  (item.printCardTemplateId ?? "") === this.preferredTemplateId,
              )
            : null;
          this.selected = preferred ?? this.items[0] ?? null;
          this.syncPreviewPageSize();
          this.preferredTemplateId = null;
          if (!this.selected) {
            this.loading = false;
            this.clearPreview();
          } else if (this.employeePickerReady) {
            this.loading = false;
            this.loadPreview();
          } else {
            this.loadingPreview = true;
          }
        },
        error: () => {
          this.loading = false;
          this.clearPreview();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.previewTrigger$.complete();
  }

  get canManageTemplates(): boolean {
    return this.auth.isSuperAdmin;
  }

  get canApproveSelected(): boolean {
    if (!this.selected?.isPendingApproval) {
      return false;
    }
    const approverEmail =
      this.selected.approval?.approverEmail?.trim().toLowerCase() || "";
    const currentEmail = this.auth.appUser?.email?.trim().toLowerCase() || "";
    return approverEmail.length > 0 && approverEmail === currentEmail;
  }

  get canRejectSelected(): boolean {
    if (!this.selected?.isPendingApproval) {
      return false;
    }
    const currentEmail = this.auth.appUser?.email?.trim().toLowerCase() || "";
    const approverEmail =
      this.selected.approval?.approverEmail?.trim().toLowerCase() || "";
    const requesterEmail =
      this.selected.approval?.requestedByEmail?.trim().toLowerCase() || "";
    return (
      this.auth.isSuperAdmin ||
      (approverEmail.length > 0 && approverEmail === currentEmail) ||
      (requesterEmail.length > 0 && requesterEmail === currentEmail)
    );
  }

  get selectedPreviewPageWidthCm(): number | null {
    return this.previewPageWidthCm;
  }

  get selectedPreviewPageHeightCm(): number | null {
    return this.previewPageHeightCm;
  }

  get pickerTemplateContextId(): string | null {
    return "print-cards";
  }

  get previewPageSizePresets(): PrintCardPageSizePreset[] {
    if (!this.selected) {
      return [];
    }
    return printCardPageSizePresetsForTemplate(
      this.pageSizePresets,
      this.selected.type,
      this.selected.settings?.allowedPageSizePresetIds,
    );
  }

  private layoutFromSelection(): {
    pageWidthCm: number;
    pageHeightCm: number;
  } | null {
    const preset =
      this.previewPageSizePresets.find(
        (item) => item.id === this.previewPageSizePresetId,
      ) ?? this.previewPageSizePresets[0];
    if (!preset) {
      return null;
    }
    const portrait =
      this.selected?.settings?.orientation === "portrait" ||
      this.selected?.settings?.designDocument?.orientation === "portrait";
    return {
      pageWidthCm: portrait ? preset.heightCm : preset.widthCm,
      pageHeightCm: portrait ? preset.widthCm : preset.heightCm,
    };
  }

  private selectedPdfPresetId(): string {
    return (
      this.previewPageSizePresetId ||
      this.previewPageSizePresets[0]?.id ||
      defaultPrintCardPageSizePresetId(this.selected?.type ?? "PAPER")
    );
  }

  createTemplate(): void {
    void this.router.navigate(["/print-cards/create"]);
  }

  editTemplate(): void {
    const templateId = this.selected?.printCardTemplateId?.trim();
    if (!templateId || !this.canManageTemplates) return;
    void this.router.navigate(["/print-cards", templateId, "edit"]);
  }

  duplicateTemplate(): void {
    const sourceId = this.selected?.printCardTemplateId?.trim();
    if (!sourceId || !this.canManageTemplates || this.duplicating) {
      return;
    }
    this.duplicating = true;
    this.printCardService
      .duplicateTemplate(sourceId)
      .pipe(finalize(() => (this.duplicating = false)))
      .subscribe({
        next: (created) => {
          this.items = [...this.items, created];
          this.selected = created;
          this.syncPreviewPageSize();
          this.loadPreview();
          this.toastr.success(
            this.translate.instant("printCards.duplicateSuccess") as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("printCards.duplicateFailed") as string,
          );
        },
      });
  }

  select(item: PrintCardTemplateItem): void {
    if (
      (this.selected?.printCardTemplateId ?? "") ===
      (item.printCardTemplateId ?? "")
    ) {
      return;
    }
    this.selected = item;
    this.syncPreviewPageSize();
    this.loadPreview();
  }

  isSelected(item: PrintCardTemplateItem): boolean {
    return (
      (this.selected?.printCardTemplateId ?? "") ===
      (item.printCardTemplateId ?? "")
    );
  }

  onPreviewPageSizeChange(pageSizePresetId: string): void {
    this.previewPageSizePresetId = pageSizePresetId;
    this.loadPreview();
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.employeePickerReady = true;
    this.loading = false;
    this.loadPreview();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.loading = false;
    if (this.selected) {
      this.loadPreview();
    }
  }

  get previewBusy(): boolean {
    return this.loadingPreview;
  }

  get listBusy(): boolean {
    return this.loading;
  }

  get contentReady(): boolean {
    return !this.previewBusy;
  }

  get mainPhase(): CanvasListMainPhase {
    return canvasListMainPhase({
      listBusy: this.listBusy,
      hasSelection: !!this.selected,
      contentReady: this.contentReady,
    });
  }

  get mainReady(): boolean {
    return this.mainPhase === "ready";
  }

  get showSyncing(): boolean {
    if (!this.selected) {
      return false;
    }
    return (
      this.previewBusy &&
      !this.previewDocumentHtml &&
      !this.previewBackDocumentHtml
    );
  }

  setActive(item: PrintCardTemplateItem, isActive: boolean): void {
    const templateId = item.printCardTemplateId?.trim();
    if (
      !templateId ||
      !this.canManageTemplates ||
      this.activeUpdatingTemplateId ||
      this.previewUpdatingTemplateId ||
      this.approvalUpdatingTemplateId
    ) {
      return;
    }
    if (item.isActive === isActive && !item.isPendingApproval) {
      return;
    }
    if (!isActive && item.isPendingApproval) {
      this.rejectTemplate(item);
      return;
    }
    this.activeUpdatingTemplateId = templateId;
    this.printCardService
      .setTemplateActive(templateId, isActive)
      .pipe(finalize(() => (this.activeUpdatingTemplateId = null)))
      .subscribe({
        next: (updated) => {
          this.replaceTemplate(updated);
          this.toastr.success(
            this.translate.instant(
              updated.isPendingApproval
                ? "printCards.pending.submittedSuccess"
                : "printCards.active.updateSuccess",
            ) as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("printCards.active.updateFailed") as string,
          );
        },
      });
  }

  setPreview(item: PrintCardTemplateItem, isPreview: boolean): void {
    const templateId = item.printCardTemplateId?.trim();
    if (
      !templateId ||
      !this.canManageTemplates ||
      this.activeUpdatingTemplateId ||
      this.previewUpdatingTemplateId ||
      this.approvalUpdatingTemplateId
    ) {
      return;
    }
    if (item.isPreview === isPreview) {
      return;
    }
    this.previewUpdatingTemplateId = templateId;
    this.printCardService
      .setTemplatePreview(templateId, isPreview)
      .pipe(finalize(() => (this.previewUpdatingTemplateId = null)))
      .subscribe({
        next: (updated) => {
          this.replaceTemplate(updated);
          this.toastr.success(
            this.translate.instant(
              "printCards.preview.updateSuccess",
            ) as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("printCards.preview.updateFailed") as string,
          );
        },
      });
  }

  approveTemplate(item: PrintCardTemplateItem): void {
    const templateId = item.printCardTemplateId?.trim();
    if (
      !templateId ||
      !this.canApproveSelected ||
      templateId !== (this.selected?.printCardTemplateId ?? "") ||
      this.approvalUpdatingTemplateId ||
      this.activeUpdatingTemplateId ||
      this.previewUpdatingTemplateId
    ) {
      return;
    }
    this.approvalUpdatingTemplateId = templateId;
    this.printCardService
      .approveTemplate(templateId)
      .pipe(finalize(() => (this.approvalUpdatingTemplateId = null)))
      .subscribe({
        next: (updated) => {
          this.replaceTemplate(updated);
          this.toastr.success(
            this.translate.instant(
              "printCards.pending.approveSuccess",
            ) as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant(
              "printCards.pending.approveFailed",
            ) as string,
          );
        },
      });
  }

  rejectTemplate(item: PrintCardTemplateItem): void {
    const templateId = item.printCardTemplateId?.trim();
    const currentEmail = this.auth.appUser?.email?.trim().toLowerCase() || "";
    const approverEmail =
      item.approval?.approverEmail?.trim().toLowerCase() || "";
    const requesterEmail =
      item.approval?.requestedByEmail?.trim().toLowerCase() || "";
    const allowed =
      !!item.isPendingApproval &&
      (this.auth.isSuperAdmin ||
        (approverEmail.length > 0 && approverEmail === currentEmail) ||
        (requesterEmail.length > 0 && requesterEmail === currentEmail));
    if (
      !templateId ||
      !allowed ||
      this.approvalUpdatingTemplateId ||
      this.activeUpdatingTemplateId ||
      this.previewUpdatingTemplateId
    ) {
      return;
    }
    this.approvalUpdatingTemplateId = templateId;
    this.printCardService
      .rejectTemplate(templateId)
      .pipe(finalize(() => (this.approvalUpdatingTemplateId = null)))
      .subscribe({
        next: (updated) => {
          this.replaceTemplate(updated);
          this.toastr.success(
            this.translate.instant(
              "printCards.pending.rejectSuccess",
            ) as string,
          );
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("printCards.pending.rejectFailed") as string,
          );
        },
      });
  }

  private replaceTemplate(updated: PrintCardTemplateItem): void {
    this.items = this.items.map((template) =>
      (template.printCardTemplateId ?? "") ===
      (updated.printCardTemplateId ?? "")
        ? { ...template, ...updated }
        : template,
    );
    if (
      (this.selected?.printCardTemplateId ?? "") ===
      (updated.printCardTemplateId ?? "")
    ) {
      this.selected = { ...this.selected, ...updated };
    }
  }

  downloadPdf(): void {
    const selected = this.selected;
    const templateId = selected?.printCardTemplateId?.trim();
    if (!templateId || this.downloading || this.downloadingPrinter) return;
    this.openPdfFormatDialog((pageSizePresetId) => {
      const email = this.previewEmail();
      if (!email) return;
      this.downloading = true;
      this.printCardService
        .downloadPdf(templateId, email, pageSizePresetId)
        .pipe(finalize(() => (this.downloading = false)))
        .subscribe({
          next: ({ blob, filename }) => {
            this.triggerBlobDownload(blob, filename);
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("printCards.downloadPdfFailed") as string,
            );
          },
        });
    });
  }

  downloadPrinterPdf(): void {
    const selected = this.selected;
    const templateId = selected?.printCardTemplateId?.trim();
    if (
      !this.auth.isSuperAdmin ||
      !templateId ||
      this.downloading ||
      this.downloadingPrinter
    ) {
      return;
    }
    this.openPdfFormatDialog((pageSizePresetId) => {
      const email = this.previewEmail();
      if (!email) return;
      this.downloadingPrinter = true;
      this.printCardService
        .downloadPrinterPdf(templateId, email, pageSizePresetId)
        .pipe(finalize(() => (this.downloadingPrinter = false)))
        .subscribe({
          next: ({ blob, filename }) => {
            this.triggerBlobDownload(blob, filename);
          },
          error: () => {
            this.toastr.error(
              this.translate.instant(
                "printCards.downloadPrinterPdfFailed",
              ) as string,
            );
          },
        });
    });
  }

  downloadAllEmployeesPdf(): void {
    const selected = this.selected;
    const templateId = selected?.printCardTemplateId?.trim();
    if (
      !templateId ||
      this.downloading ||
      this.downloadingPrinter ||
      this.downloadingAllEmployees
    ) {
      return;
    }
    const presets = this.previewPageSizePresets;
    const dialogRef = this.dialog.open<
      PrintCardBulkDownloadDialogComponent,
      {
        presets: PrintCardPageSizePreset[];
        selectedPresetId: string;
      },
      PrintCardBulkDownloadDialogResult
    >(PrintCardBulkDownloadDialogComponent, {
      width: "720px",
      maxWidth: "95vw",
      panelClass: "app-dialog-panel",
      data: {
        presets,
        selectedPresetId: this.selectedPdfPresetId(),
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.pageSizePresetId || result.emails.length === 0) {
        return;
      }
      this.downloadingAllEmployees = true;
      this.printCardService
        .downloadBulkEmployeesPdf(
          templateId,
          result.pageSizePresetId,
          result.emails,
        )
        .pipe(finalize(() => (this.downloadingAllEmployees = false)))
        .subscribe({
          next: ({ blob, filename }) => {
            this.triggerBlobDownload(blob, filename);
          },
          error: () => {
            this.toastr.error(
              this.translate.instant(
                "printCards.downloadAllEmployeesPdfFailed",
              ) as string,
            );
          },
        });
    });
  }

  private openPdfFormatDialog(
    onConfirm: (pageSizePresetId: string) => void,
  ): void {
    const presets = this.previewPageSizePresets;
    const dialogRef = this.dialog.open<
      PrintCardPdfFormatDialogComponent,
      { presets: PrintCardPageSizePreset[]; selectedPresetId: string },
      PrintCardPdfFormatDialogResult
    >(PrintCardPdfFormatDialogComponent, {
      width: "520px",
      panelClass: "app-dialog-panel",
      data: {
        presets,
        selectedPresetId: this.selectedPdfPresetId(),
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.pageSizePresetId) {
        return;
      }
      onConfirm(result.pageSizePresetId);
    });
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  removeTemplate(): void {
    const targetId = this.selected?.printCardTemplateId?.trim();
    if (
      !targetId ||
      !this.canManageTemplates ||
      this.deleting ||
      this.selected?.hasOrders
    ) {
      return;
    }
    this.deleting = true;
    this.printCardService
      .deleteTemplate(targetId)
      .pipe(finalize(() => (this.deleting = false)))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant("printCards.templateDeleted") as string,
          );
          this.items = this.items.filter(
            (item) => (item.printCardTemplateId ?? "") !== targetId,
          );
          this.selected = this.items[0] ?? null;
          this.syncPreviewPageSize();
          if (this.selected) {
            this.loadPreview();
          } else {
            this.clearPreview();
          }
        },
        error: (error: unknown) => {
          const message = getHttpErrorMessage(error);
          this.toastr.error(
            this.translate.instant(
              message ===
                "Print card template cannot be deleted while orders exist"
                ? "printCards.templateDeleteBlockedByOrders"
                : "printCards.templateDeleteFailed",
            ) as string,
          );
        },
      });
  }

  private loadTemplates(): void {
    this.loading = true;
    forkJoin({
      templates: this.printCardService.getTemplates(),
      presets: this.printCardService
        .getPageSizePresets()
        .pipe(catchError(() => of({ presets: this.pageSizePresets }))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ templates, presets }) => {
          this.pageSizePresets = presets.presets ?? this.pageSizePresets;
          this.items = templates.templates ?? [];
          const preferred = this.preferredTemplateId
            ? this.items.find(
                (item) =>
                  (item.printCardTemplateId ?? "") === this.preferredTemplateId,
              )
            : null;
          this.selected = preferred ?? this.items[0] ?? null;
          this.syncPreviewPageSize();
          this.preferredTemplateId = null;
          if (!this.selected) {
            this.loading = false;
            this.clearPreview();
          } else if (this.employeePickerReady) {
            this.loading = false;
            this.loadPreview();
          } else {
            this.loadingPreview = true;
          }
        },
        error: () => {
          this.loading = false;
          this.clearPreview();
        },
      });
  }

  private previewEmail(): string {
    const email = this.previewEmployee?.email?.trim();
    return email && email.length > 0 ? email : (this.auth.appUser?.email ?? "");
  }

  private clearPreview(): void {
    this.loadingPreview = false;
    this.previewDocumentHtml = null;
    this.previewBackDocumentHtml = null;
    this.previewPageWidthCm = null;
    this.previewPageHeightCm = null;
    this.previewTrigger$.next(null);
  }

  private loadPreview(): void {
    const templateId = this.selected?.printCardTemplateId?.trim();
    if (!templateId || templateId === "undefined") {
      this.clearPreview();
      return;
    }
    if (!this.employeePickerReady) {
      this.loadingPreview = true;
      return;
    }
    const email = this.previewEmail();
    if (!email) {
      this.clearPreview();
      return;
    }
    this.loadingPreview = true;
    this.previewTrigger$.next({
      templateId,
      email,
      ...(this.previewPageSizePresetId
        ? { pageSizePresetId: this.previewPageSizePresetId }
        : {}),
    });
  }

  private syncPreviewPageSize(): void {
    const enabled = this.previewPageSizePresets;
    if (enabled.length === 0) {
      this.previewPageSizePresetId = null;
      return;
    }
    if (!enabled.some((preset) => preset.id === this.previewPageSizePresetId)) {
      this.previewPageSizePresetId = enabled[0].id;
    }
  }
}
